import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Loader2, Search, Building2, PoundSterling, CheckCircle2, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (n) => `£${(n || 0).toLocaleString('en-GB')}`;
const SRC_LABEL = { companies_house: 'Companies House', manual: 'Manual', default: 'Default' };
const SRC_C = { companies_house: 'bg-blue-100 text-blue-700', manual: 'bg-violet-100 text-violet-700', default: 'bg-gray-100 text-gray-500' };

const VendorSubscriptions = () => {
  const [bands, setBands] = useState([]);
  const [sum, setSum] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [bandFilter, setBandFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const pageSize = 25;

  const loadMeta = useCallback(async () => {
    const [b, s] = await Promise.all([
      axios.get(`${API}/vendor-subscriptions/bands`),
      axios.get(`${API}/vendor-subscriptions/summary`),
    ]);
    setBands(b.data.bands || []); setSum(s.data);
  }, []);

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/vendor-subscriptions/suppliers`, {
        params: { search: search || undefined, band: bandFilter === 'all' ? undefined : bandFilter, page, page_size: pageSize },
      });
      setItems(data.items || []); setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [search, bandFilter, page]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { const t = setTimeout(loadSuppliers, 250); return () => clearTimeout(t); }, [loadSuppliers]);

  const changeBand = async (id, band) => {
    try {
      await axios.post(`${API}/vendor-subscriptions/suppliers/${id}/band`, { band });
      toast.success('Band updated');
      loadSuppliers(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleVerified = async (id, verified) => {
    try {
      await axios.post(`${API}/vendor-subscriptions/suppliers/${id}/verified`, { verified });
      toast.success(verified ? 'Marked verified' : 'Verified removed');
      loadSuppliers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const bulkSelected = async (verified) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await axios.post(`${API}/vendor-subscriptions/bulk-verified`, { verified, supplier_ids: ids });
      toast.success(`${verified ? 'Verified' : 'Unverified'} ${data.updated} supplier(s)`);
      setSelected(new Set()); loadSuppliers(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const bulkAllMatching = async (verified) => {
    const label = bandFilter === 'all' && !search ? 'ALL 783 suppliers' : 'all suppliers matching the current filter';
    if (!window.confirm(`${verified ? 'Verify' : 'Unverify'} ${label}?`)) return;
    try {
      const { data } = await axios.post(`${API}/vendor-subscriptions/bulk-verified`, {
        verified, all_matching: true, search: search || null, band: bandFilter === 'all' ? null : bandFilter });
      toast.success(`${verified ? 'Verified' : 'Unverified'} ${data.updated} supplier(s)`);
      setSelected(new Set()); loadSuppliers(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const allOnPageSelected = items.length > 0 && items.every((s) => selected.has(s.id));
  const toggleSelectPage = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allOnPageSelected) items.forEach((s) => n.delete(s.id));
      else items.forEach((s) => n.add(s.id));
      return n;
    });
  };

  const backfill = async () => {
    const { data } = await axios.post(`${API}/vendor-subscriptions/backfill-defaults`);
    toast.success(`Defaulted ${data.updated} suppliers`);
    loadSuppliers(); loadMeta();
  };

  const pages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="vendor-subscriptions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Supplier subscriptions</h1>
          <p className="text-gray-500 text-sm mt-1">Size-banded pricing. Bands are estimated from Companies House and can be overridden per supplier.</p>
        </div>
        <Button variant="outline" onClick={backfill} data-testid="backfill-btn">Set default band on unbanded</Button>
      </div>

      {sum && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="subs-summary">
            <Card><CardContent className="p-4"><div className="flex items-center justify-between"><p className="text-xs text-gray-500">Suppliers</p><Building2 className="w-4 h-4 text-[#00A79D]" /></div><p className="text-2xl font-bold text-gray-900 mt-1">{sum.total_suppliers}</p></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center justify-between"><p className="text-xs text-gray-500">Potential MRR</p><PoundSterling className="w-4 h-4 text-[#F5871F]" /></div><p className="text-2xl font-bold text-[#00857D] mt-1">{gbp(sum.potential_mrr)}</p></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center justify-between"><p className="text-xs text-gray-500">Banded via CH</p><CheckCircle2 className="w-4 h-4 text-blue-600" /></div><p className="text-2xl font-bold text-gray-900 mt-1">{sum.banded_from_companies_house}</p></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center justify-between"><p className="text-xs text-gray-500">Overridden</p></div><p className="text-2xl font-bold text-gray-900 mt-1">{sum.manually_overridden}</p></CardContent></Card>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="band-distribution">
            {bands.map((b) => {
              const d = sum.by_band[b.key] || { count: 0 };
              return (
                <button key={b.key} onClick={() => { setBandFilter(bandFilter === b.key ? 'all' : b.key); setPage(1); }}
                  className={`border rounded-lg px-3 py-2 text-left transition-colors ${bandFilter === b.key ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-gray-200 hover:border-[#00A79D]/40'}`} data-testid={`band-chip-${b.key}`}>
                  <p className="text-xs font-semibold text-gray-800">{b.label}</p>
                  <p className="text-[11px] text-gray-500">{b.staff_label} · {gbp(b.monthly)}/mo</p>
                  <p className="text-lg font-bold text-gray-900">{d.count}</p>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input className="pl-9" placeholder="Search suppliers" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="subs-search" />
        </div>
        {bandFilter !== 'all' && <Button variant="ghost" size="sm" onClick={() => setBandFilter('all')}>Clear filter</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5" data-testid="bulk-bar">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectPage} data-testid="select-page" className="w-4 h-4 accent-[#00A79D]" />
          Select page
        </label>
        {selected.size > 0 && (
          <>
            <span className="text-sm font-medium text-gray-700">{selected.size} selected</span>
            <Button size="sm" className="h-8 bg-[#00A79D] hover:bg-[#00857D]" onClick={() => bulkSelected(true)} data-testid="bulk-verify-selected"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verify selected</Button>
            <Button size="sm" variant="outline" className="h-8" onClick={() => bulkSelected(false)} data-testid="bulk-unverify-selected">Unverify selected</Button>
            <Button size="sm" variant="ghost" className="h-8 text-gray-500" onClick={() => setSelected(new Set())}>Clear</Button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-400">Whole filter:</span>
          <Button size="sm" variant="outline" className="h-8 border-[#00A79D] text-[#00857D]" onClick={() => bulkAllMatching(true)} data-testid="bulk-verify-all">Verify all {bandFilter !== 'all' || search ? 'matching' : ''}</Button>
          <Button size="sm" variant="ghost" className="h-8 text-gray-500" onClick={() => bulkAllMatching(false)} data-testid="bulk-unverify-all">Unverify all</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
      ) : (
        <div className="space-y-2" data-testid="subs-supplier-list">
          {items.length === 0 && <div className="border border-dashed border-gray-200 rounded-lg py-14 text-center text-gray-400">No suppliers match.</div>}
          {items.map((s) => {
            const sub = s.subscription || {};
            return (
              <div key={s.id} className="border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`subs-row-${s.id}`}>
                <div className="min-w-0 flex items-center gap-3">
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} data-testid={`subs-select-${s.id}`} className="w-4 h-4 accent-[#00A79D] shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{s.name}</p>
                    <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                      {sub.employee_estimate != null && <span>~{sub.employee_estimate} staff (est.)</span>}
                      {sub.employee_source && <Badge className={`${SRC_C[sub.employee_source]} text-[10px]`}>{SRC_LABEL[sub.employee_source]}</Badge>}
                      {s.companies_house_number && <span className="text-gray-400">CH {s.companies_house_number}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    onClick={() => toggleVerified(s.id, !s.verified)}
                    data-testid={`verified-toggle-${s.id}`}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border transition-colors ${s.verified ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#00A79D]'}`}>
                    <ShieldCheck className="w-3.5 h-3.5" /> {s.verified ? 'Verified' : 'Verify'}
                  </button>
                  <span className="text-sm font-medium text-gray-800">{gbp(sub.monthly)}<span className="text-gray-400 text-xs">/mo</span></span>
                  <Select value={sub.band || 'small'} onValueChange={(v) => changeBand(s.id, v)}>
                    <SelectTrigger className="w-[180px] h-9" data-testid={`subs-band-select-${s.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {bands.map((b) => <SelectItem key={b.key} value={b.key}>{b.label} · {b.staff_label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="subs-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="subs-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
};

export default VendorSubscriptions;
